import React from 'react';
import type { ReactElement } from 'react';

import type { Node } from '@atlaskit/editor-prosemirror/model';

import { estimateMergedRowHeight } from './rowHeightEstimate.ts';
import { NumberColumnCell } from './TableCell.tsx';

/** A table row merged into one cell: its searchable text and the block local IDs inside it. */
export type TextRowData = {
	columnCount: number;
	// One node per type in the row, to preload the full components the upgrade will render.
	contentNodes: readonly Node[];
	estimatedHeight: number;
	isHeader: boolean;
	// Block-link navigation looks up these IDs in the DOM.
	localIds: readonly string[];
	text: string;
};

const SEPARATOR = ' ';

const leafTextFor =
	(allowPlaceholderText: boolean) =>
	(leaf: Node): string => {
		switch (leaf.type.name) {
			case 'hardBreak':
				return SEPARATOR;
			case 'placeholder':
				return allowPlaceholderText ? leaf.attrs.text || '' : '';
			case 'status': {
				const statusText: string = leaf.attrs.text || '';
				return leaf.attrs.style === 'mixedCase' ? statusText : statusText.toUpperCase();
			}
			case 'inlineCard':
			case 'blockCard':
			case 'embedCard':
				return leaf.attrs.url || '';
			default:
				return typeof leaf.attrs.text === 'string' ? leaf.attrs.text : '';
		}
	};

const textRows = new WeakMap<Node, Map<string, TextRowData>>();

/** The row's merged text row, cached per row node. */
export function getTextRowData(
	row: Node,
	allowPlaceholderText = false,
	columnWidths: readonly number[],
): TextRowData {
	const cacheKey = `${allowPlaceholderText}:${columnWidths.join(',')}`;
	let cache = textRows.get(row);
	const cached = cache?.get(cacheKey);
	if (cached) {
		return cached;
	}
	const contentNodes = new Map<string, Node>();
	const localIds: string[] = [];
	const leafText = leafTextFor(allowPlaceholderText);
	const texts: string[] = [];
	let isHeader = row.childCount > 0;
	row.forEach((cell) => {
		isHeader &&= cell.type.name === 'tableHeader';
		cell.descendants((node) => {
			if (!contentNodes.has(node.type.name)) {
				contentNodes.set(node.type.name, node);
			}
			if (typeof node.attrs.localId === 'string' && node.attrs.localId) {
				localIds.push(node.attrs.localId);
			}
			return true;
		});
		texts.push(cell.textBetween(0, cell.content.size, SEPARATOR, leafText));
	});
	const data: TextRowData = {
		columnCount: columnWidths.length,
		contentNodes: [...contentNodes.values()],
		estimatedHeight: estimateMergedRowHeight(row, columnWidths, leafText),
		isHeader,
		localIds,
		text: texts.filter(Boolean).join(SEPARATOR),
	};
	if (!cache) {
		cache = new Map();
		textRows.set(row, cache);
	}
	cache.set(cacheKey, data);
	return data;
}

/** Stands in for a row's content, which is not serialized until the table upgrades. */
export const TextRow = ({ row }: { row: TextRowData }): ReactElement => {
	const Cell = row.isHeader ? 'th' : 'td';
	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- The estimated height is computed per row.
		<tr data-light-text-row style={{ height: row.estimatedHeight }}>
			<NumberColumnCell />
			<Cell colSpan={row.columnCount}>
				{/* Block links find their target by `data-local-id`. Scrolling to a block in this row brings the table into view, which upgrades it. */}
				{row.localIds.map((localId) => (
					<span key={localId} data-local-id={localId} />
				))}
				{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text -- Match the paragraph spacing of rendered cell content. */}
				<p>{row.text || ' '}</p>
			</Cell>
		</tr>
	);
};
